import React, { useState } from 'react';
import { Trophy, X } from 'lucide-react';
import { storage } from '../../lib/firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { FadeInImage } from './FadeInImage';

interface RankingPrize {
  id: string;
  text?: string;
  imageUrl?: string;
  description?: string;
  groupKey: string;
  position: number;
}

interface PrizeEditModalProps {
  groupKey: string;
  position: number;
  currentPrize?: RankingPrize;
  onClose: () => void;
  onSave: (text: string, imageUrl: string, description: string) => Promise<void>;
  onDelete?: () => Promise<void>;
}

// Client-side image compression helper using HTML Canvas
const compressImage = (base64Str: string, maxWidth = 300, maxHeight = 300, quality = 0.6): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Resizing logic maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        // Compress to tiny size JPEG
        resolve(canvas.toDataURL('image/jpeg', quality));
      } else {
        resolve(base64Str);
      }
    };
    img.onerror = () => {
      resolve(base64Str);
    };
  });
};

export function PrizeEditModal({ 
  groupKey, 
  position, 
  currentPrize, 
  onClose, 
  onSave,
  onDelete 
}: PrizeEditModalProps) {
  const [text, setText] = useState(currentPrize?.text || '');
  const [imageUrl, setImageUrl] = useState(currentPrize?.imageUrl || '');
  const [description, setDescription] = useState(currentPrize?.description || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLoading(true);
      setError('');
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const rawBase64 = reader.result as string;
          // Compresses to maximum 300px bounds with 0.6 JPEG quality (under 15KB!)
          const compressed = await compressImage(rawBase64, 300, 300, 0.6);
          setImageUrl(compressed);
        } catch (err) {
          console.error(err);
          setError('Error al procesar y comprimir la imagen.');
        } finally {
          setLoading(false);
        }
      };
      reader.onerror = () => {
        setError('Error al leer el archivo.');
        setLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      let finalImageUrl = imageUrl;
      if (finalImageUrl.startsWith('data:image/')) {
        const imageRef = ref(storage, `prizes/${groupKey}_${position}_${Date.now()}.jpg`);
        await uploadString(imageRef, finalImageUrl, 'data_url');
        finalImageUrl = await getDownloadURL(imageRef);
      }
      await onSave(text, finalImageUrl, description);
      onClose();
    } catch (err: any) {
      console.error(err);
      setError('Error al guardar el premio.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
      <div className="relative w-full max-w-md bg-[#0f172a]/95 backdrop-blur-2xl border border-white/10 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 pb-4 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#CD7F32]/20 flex items-center justify-center">
              <Trophy className="text-[#CD7F32]" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-black uppercase tracking-tight text-white leading-none">
                Editar Premio #{position}
              </h3>
              <p className="text-[9px] font-bold opacity-40 uppercase tracking-widest mt-1">
                {groupKey}
              </p>
            </div>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            className="p-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-all text-white"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase opacity-40 ml-1 text-white">Nombre del Premio</label>
            <input
              type="text"
              required
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ej: Paleta Bullpadel Vertex 04"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#CD7F32]/50 font-bold text-xs transition-all text-white"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase opacity-40 ml-1 text-white">Descripción Detallada</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Entrega inmediata en el club. Válido para el jugador del ranking..."
              rows={3}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#CD7F32]/50 font-bold text-xs transition-all text-white resize-none"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase opacity-40 ml-1 text-white">Imagen del Premio</label>
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/5">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
                {imageUrl ? (
                  <FadeInImage src={imageUrl} alt="Prize Preview" className="w-full h-full object-cover" />
                ) : (
                  <Trophy size={24} className="opacity-20 text-white" />
                )}
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <label className="px-3 py-1.5 bg-white/10 hover:bg-white/15 rounded-lg font-black uppercase text-[9px] text-center cursor-pointer transition-all text-white">
                  Subir Imagen
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </label>
                {imageUrl && (
                  <button
                    type="button"
                    onClick={() => setImageUrl('')}
                    className="text-[9px] font-black uppercase text-red-400 hover:text-red-300 transition-all text-left self-start"
                  >
                    Eliminar Imagen
                  </button>
                )}
              </div>
            </div>
            
            <div className="pt-2">
              <label className="text-[9px] font-black uppercase opacity-40 ml-1 text-white">O pegar URL de imagen</label>
              <input
                type="text"
                value={imageUrl.startsWith('data:') ? '' : imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#CD7F32]/50 font-bold text-xs transition-all text-white mt-1"
              />
            </div>
          </div>

          {error && (
            <p className="text-[10px] text-red-500 font-black uppercase text-center">{error}</p>
          )}

          {/* Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-white/5 hover:bg-white/10 py-3 rounded-xl font-black uppercase text-xs transition-all text-white"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-[#CD7F32] to-[#B87333] hover:brightness-110 text-white py-3 rounded-xl font-black uppercase text-xs shadow-lg transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  'Guardar'
                )}
              </button>
            </div>
            {currentPrize && onDelete && (
              <button
                type="button"
                disabled={loading}
                onClick={async () => {
                  if (await window.confirm('¿Estás seguro de que deseas eliminar este premio?')) {
                    setLoading(true);
                    setError('');
                    try {
                      await onDelete();
                      onClose();
                    } catch (err: any) {
                      console.error(err);
                      setError('Error al eliminar el premio.');
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
                className="w-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 hover:text-red-300 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all"
              >
                Eliminar Premio
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
